import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription, take, takeUntil } from 'rxjs';
import { Client } from 'src/typings';
import { NotificationService } from 'src/app/services/notification.service';
import { ClientService } from '../client.service';
import { formatClientPhoneCompact } from '../client-phone.util';
import { CLIENT_DIALOG_CONFIG, CreateComponent } from '../create/create.component';

type ClientFilters = { phone?: string; name?: string; id?: number };

@Component({ selector: 'app-client-list', templateUrl: './client-list.component.html', styleUrls: ['./client-list.component.scss'] })
export class ClientListComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(private clientService: ClientService, private fb: UntypedFormBuilder,
    private notificationService: NotificationService, private dialog: MatDialog,
    private route: ActivatedRoute, private router: Router) {}

  @ViewChild('listViewport') listViewport?: ElementRef<HTMLElement>;
  searchForm = this.fb.group({ phone: [''], name: [''], id: ['', [Validators.pattern(/^\d*$/)]] });
  displayedColumns = ['client', 'phone', 'manager', 'terms', 'actions'];
  dataSource: Client[] = [];
  resultsLength = 0;
  loading = false;
  error = '';
  pageSize = 30;
  filtersOpened = typeof window === 'undefined' || window.innerWidth > 900;
  deletingId: number | null = null;
  appliedFilters: ClientFilters = {};
  private offset = 0;
  private revision = 0;
  private destroyed = false;
  private querySubscription?: Subscription;
  private resizeObserver?: ResizeObserver;
  private frame: number | null = null;
  private activeDialog = false;

  get hasMore() { return this.offset < this.resultsLength; }
  get activeFilterCount() { return Object.keys(this.appliedFilters).length; }

  ngOnInit() {
    this.querySubscription = this.route.queryParamMap.subscribe(params => {
      const id = Number(params.get('id'));
      this.appliedFilters = this.normalizeFilters({ phone: params.get('phone'), name: params.get('name'),
        id: Number.isSafeInteger(id) && id > 0 ? id : undefined });
      this.searchForm.reset({ phone: this.appliedFilters.phone || '', name: this.appliedFilters.name || '',
        id: this.appliedFilters.id ? String(this.appliedFilters.id) : '' }, { emitEvent: false });
      this.reload();
    });
  }

  ngAfterViewInit() {
    if (typeof ResizeObserver !== 'undefined' && this.listViewport) {
      this.resizeObserver = new ResizeObserver(() => this.checkEdge());
      this.resizeObserver.observe(this.listViewport.nativeElement);
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.revision++;
    this.querySubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
    if (this.frame !== null) cancelAnimationFrame(this.frame);
  }

  search() {
    if (this.searchForm.invalid) { this.searchForm.markAllAsTouched(); return; }
    const next = this.normalizeFilters(this.searchForm.value);
    if (JSON.stringify(next) === JSON.stringify(this.appliedFilters)) this.reload();
    else this.router.navigate([], { relativeTo: this.route, queryParams: next });
  }

  reset() {
    this.searchForm.reset({ phone: '', name: '', id: '' });
    if (!this.activeFilterCount) this.reload();
    else this.router.navigate([], { relativeTo: this.route, queryParams: {} });
  }

  reload() {
    this.revision++;
    this.dataSource = [];
    this.resultsLength = 0;
    this.offset = 0;
    this.error = '';
    this.loading = false;
    if (this.listViewport) this.listViewport.nativeElement.scrollTop = 0;
    void this.loadMore();
  }

  async loadMore() {
    if (this.destroyed || this.loading || (this.offset > 0 && !this.hasMore)) return;
    const revision = this.revision;
    this.loading = true;
    this.error = '';
    try {
      const result = await firstValueFrom(this.clientService.search({ ...this.appliedFilters,
        offset: this.offset, limit: this.pageSize }));
      if (this.destroyed || revision !== this.revision) return;
      const known = new Set(this.dataSource.map(client => client.id));
      const next = result.data.filter(client => { if (known.has(client.id)) return false; known.add(client.id); return true; });
      this.dataSource = [...this.dataSource, ...next];
      this.offset += result.data.length;
      this.resultsLength = Number(result.length) || 0;
      if (!result.data.length) this.offset = this.resultsLength;
    } catch {
      if (!this.destroyed && revision === this.revision) this.error = 'Не удалось загрузить клиентов. Попробуйте ещё раз.';
    } finally {
      if (!this.destroyed && revision === this.revision) { this.loading = false; this.scheduleEdgeCheck(); }
    }
  }

  @HostListener('window:resize')
  checkEdge() {
    const viewport = this.listViewport?.nativeElement;
    if (viewport && viewport.clientHeight > 0 && !this.loading && !this.error && this.hasMore &&
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 160) void this.loadMore();
  }

  private scheduleEdgeCheck() {
    if (typeof requestAnimationFrame === 'undefined') return;
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => { this.frame = null; this.checkEdge(); });
  }

  async delete(client: Client) {
    if (this.deletingId !== null || !window.confirm(`Удалить клиента «${client.name || 'Без имени'}» №${client.id}?`)) return;
    this.deletingId = client.id;
    try {
      await firstValueFrom(this.clientService.delete(client.id));
      this.notificationService.notice('Клиент удалён');
      this.reload();
    } catch { this.notificationService.showError('Не удалось удалить клиента. Проверьте связанные документы.'); }
    finally { this.deletingId = null; }
  }

  create() {
    if (this.activeDialog) return;
    this.activeDialog = true;
    const ref = this.dialog.open(CreateComponent, CLIENT_DIALOG_CONFIG);
    ref.componentInstance.nameFormControl.setValue(this.searchForm.value.name || '');
    ref.componentInstance.phoneFormControl.setValue(this.searchForm.value.phone || '');
    ref.componentInstance.createClient.pipe(take(1), takeUntil(ref.afterClosed())).subscribe(client => {
      ref.close(); this.router.navigate(['/client/profile', client.id]);
    });
    ref.afterClosed().pipe(take(1)).subscribe(() => this.activeDialog = false);
  }

  getPhoneSummary(client: Client) { return formatClientPhoneCompact(client); }
  trackClient(_: number, client: Client) { return client.id; }

  private normalizeFilters(value: any): ClientFilters {
    const result: ClientFilters = {};
    if (String(value.phone || '').trim()) result.phone = String(value.phone).trim();
    if (String(value.name || '').trim()) result.name = String(value.name).trim();
    const id = Number(value.id);
    if (Number.isSafeInteger(id) && id > 0) result.id = id;
    return result;
  }
}
